/* SiteInstanceEditor.js */

"use strict";
const GameEditor = require("@gluenetworks/game_editor");
const _ = require("lodash");

class SiteInstanceEditor extends GameEditor {
    constructor(
        logger,
        di,
        editorState,
        editorLayoutMgr,
        editorLayout,
        userSession,
        editorTabCmpnt,
        solutionsManager,
        steveIo,
        storageFactory,
        SiteEntity
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._editorLayout = editorLayout;
        this._tabCmpnt = editorTabCmpnt;
        this._editorTabState = editorState;
        this._userSession = userSession;
        this._activeOrgId = userSession.activeOrganization.id;

        let solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = solutionDefinition.name || "";
        let siteEditorViews = _.get(solutionDefinition, "siteEditor.viewNamespaces", []);
        let siteEditorActions = _.get(solutionDefinition, "siteEditor.actionNamespaces", []);

        let launchOptions = editorState.launchOptions;

        this._siteTypeIcon = "siteexplorer.site";

        this._siteEntity = SiteEntity;
        this.currentEntityRemoved = false;
        this.currentEntityDeleteInProgress = false;
        this._storageFactory = storageFactory;
        this._userDataStorage = this._storageFactory.getRemoteStorageInstance(
            "view",
            "site_details"
        );
        this.setNamespaceGlobs(siteEditorViews.concat(siteEditorActions), true);
        this._instanceEditorState = {};
        this._siteProm = undefined;
        this._tabId = launchOptions.id || "Unknown";

        this.retardActions(true);

        // If we define the launching entity id
        const siteIds = [].concat(launchOptions.siteIds || []); // TODO to be replaced

        this._initSiteProm(siteIds);
        this._registerViewStateListener();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerViewStateListener() {
        this.on("ClearViewState", (this._onClearViewState = this._onClearViewState.bind(this)));
    }

    _unRegisterCabinetListener() {
        this.removeListener("ClearViewState", this._onClearViewState);
    }

    _unRegisterViewStateListener() {
        this.removeAllListeners("ClearViewState");
    }

    _registerListenersOnSite() {
        this._site.on(
            "NamePropertyUpdated",
            (this._onNamePropertyUpdated = this._onNamePropertyUpdated.bind(this))
        );
        this._site.on(
            "LocationTypePropertyUpdated",
            (this._onLocationTypePropertyUpdated = this._onLocationTypePropertyUpdated.bind(this))
        );
        this._site.on(
            "EntityDeletedFromSystem",
            (this._onSiteRemoved = this._onSiteRemoved.bind(this))
        );
        this._site.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _unregisterListenersOnSite() {
        this._site.removeListener("NamePropertyUpdated", this._onNamePropertyUpdated);
        this._site.removeListener(
            "LocationTypePropertyUpdated",
            this._onLocationTypePropertyUpdated
        );
        this._site.removeListener("EntityDeletedFromSystem", this._onSiteRemoved);
        this._site.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
    }

    _onClearViewState() {
        _.delay(() => (this.editorState.viewState = undefined), 50);
    }

    _onDirtyStateChanged(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    _onNamePropertyUpdated() {
        this.updateTab(this._site.pristineProperties.name);
    }

    _onLocationTypePropertyUpdated() {
        let pristineSite = this._site.pristineProperties.locationType;
        this.updateTabIcon("siteexplorer." + (_.snakeCase(pristineSite) || "site"));
    }

    _onSiteRemoved() {
        this.currentEntityRemoved = true;
        if (!this.currentEntityDeleteInProgress) {
            this.currentEntityRemoved = false;
            this.closeEditor(this.editorTabStateId, true);
        }
    }

    _initSiteProm(siteIds) {
        if (siteIds.length === 1) {
            let siteId = siteIds[0];
            this._siteProm = Promise.resolve()
                .then(() => {
                    return this._siteEntity.retrieveSite(this._activeOrgId, siteId, {});
                })
                .then((site) => {
                    this._site = site;
                    this._registerListenersOnSite();

                    this._siteType = this._site.locationType;

                    if (this._siteType === undefined) {
                        this._siteTypeIcon = "siteexplorer.site";
                    } else {
                        this._siteTypeIcon =
                            "siteexplorer." +
                            _.snakeCase(this._site.getLocationTypeLabels()[this._siteType]);
                    }

                    this._tabCmpnt.setEditorIconName(this._siteTypeIcon);

                    return Promise.resolve();
                });
        } else if (siteIds.length > 1) {
            this._siteProm = this._initMultipleSites(siteIds);
            this._tabCmpnt.setTitle(`Multi-Edit (${siteIds.length})`);
        }
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getSite() {
        return this._site;
    }

    get editorTabStateId() {
        return this._editorTabState.id;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenSiteIsReady() {
        if (!_.isUndefined(this._siteProm)) {
            return this._siteProm;
        } else {
            return Promise.reject("Can't load SiteInstanceEditor._siteProm");
        }
    }

    setInstanceEditorStateProperty(name, val) {
        _.set(this._instanceEditorState, name, val);
    }

    getInstanceEditorStateProperty(name) {
        return _.get(this._instanceEditorState, name);
    }

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    closeEditor(id, force) {
        id = id || this._editorTabState.id;
        return super.closeEditor(id, force);
    }

    shutdown() {
        let logger = this._logger;
        this._unRegisterCabinetListener();
        return super
            .shutdown()
            .then(() => {
                this._unRegisterViewStateListener();
                return Promise.resolve();
            })
            .then(() => _.invoke(this._pagingFeed, "shutdown") || Promise.resolve())
            .then(() => {
                if (!_.isUndefined(this._site)) {
                    this._unregisterListenersOnSite();
                    return this._site.shutdown();
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                this._tabCmpnt = undefined;
                this._site = undefined;
                this._registeredDetails = undefined;
                this._editorLayout = undefined;
                this._tabId = undefined;
                this._activeOrgId = undefined;
                this._siteProm = undefined;
                this.currentEntityRemoved = undefined;
                this.currentEntityDeleteInProgress = undefined;
                this._storageFactory = undefined;
                this._userDataStorage = undefined;
                this._pagingFeed = undefined;
                this._siteEntity = undefined;
            })
            .catch((err) => {
                logger.warn({error: err}, "SiteInstanceEditor.shutdown() error");
            });
    }

    clearPendingRelationshipsFor(key, entity) {
        let pendingRelationships = entity.getPendingRelationships();

        pendingRelationships =
            pendingRelationships &&
            pendingRelationships.filter((rel) => {
                if (rel.relName === key) {
                    return false;
                }
                return true;
            });

        entity.clearPendingRelationships(false);

        pendingRelationships.forEach((relation) => {
            entity._addPendingRelationship(
                relation.action,
                relation.relName,
                relation.rEntityType,
                relation.rEntityId
            );
        });
    }

    _initMultipleSites(siteIds = []) {
        const chunkArray = (array, chunkSize) => {
            const chunks = [];
            for (let i = 0; i < array.length; i += chunkSize) {
                chunks.push(array.slice(i, i + chunkSize));
            }
            return chunks;
        };

        const chunkedSiteIds = chunkArray(siteIds, 250);

        const loadSiteChunk = (chunk) => {
            return this._siteEntity.loadSites(this._activeOrgId, {id: {$in: chunk}});
        };

        const sitePromises = chunkedSiteIds.map((chunk) => loadSiteChunk(chunk));

        return Promise.all(sitePromises).then((results) => {
            const sites = [];
            for (const result of results) {
                sites.push(...result);
            }
            if (sites.length > 0) {
                return sites;
            } else {
                return Promise.reject("Requested sites no longer exist");
            }
        });
    }

    generateCustomFieldJtype(customFieldDef) {
        const customFieldJtype = {};
        const customFieldId = customFieldDef.id;
        const type = customFieldDef.dataType.toLowerCase();
        const label = customFieldDef.label;
        const typeInfo = customFieldDef.typeInfo || {};

        customFieldJtype[customFieldId] = "";
        customFieldJtype[customFieldId + "@Display.label"] = label
            .split(" ")
            .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
            .join(" ");

        if (_.get(customFieldDef, "source", "") === "ControllerDiscoveryConfig") {
            customFieldJtype[customFieldId + "@Display.readOnly"] = true;
        }

        if (customFieldDef.description) {
            customFieldJtype[customFieldId + "@Display.description"] = customFieldDef.description;
        }

        switch (type) {
            case "number":
                customFieldJtype[customFieldId + "@Number"] = typeInfo.Number || {};
                break;
            case "string":
                customFieldJtype[customFieldId + "@String"] = typeInfo.String || {};
                break;
            case "boolean":
                customFieldJtype[customFieldId + "@value"] = false;
                break;
            case "enum": {
                const {ids = [], labels = [], multiSelect = false} = customFieldDef.typeInfo || {};
                let fieldJtypeDefinition;
                if (multiSelect) {
                    fieldJtypeDefinition = {
                        [`${customFieldId}`]: ids,
                        [`${customFieldId}@Display.tagList.labels`]: labels,
                        [`${customFieldId}@Display.tagList.minLineCount`]: 5,
                    };
                } else {
                    fieldJtypeDefinition = {
                        [`${customFieldId}`]: null,
                        [`${customFieldId}@Enum`]: ids,
                        [`${customFieldId}@Display.enum`]: labels,
                    };
                }
                Object.assign(customFieldJtype, fieldJtypeDefinition);
                break;
            }
        }
        return customFieldJtype;
    }
}

module.exports = SiteInstanceEditor;

SiteInstanceEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "EditorLayout.EditorLayout",
    "$teve.userSession",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager",
    "$teve.io",
    "$teve.storageFactory",
    "$teve.entities.Site",
];

// Optional convenient way to set a tab icon
SiteInstanceEditor.$editorIcon = "siteexplorer.site";
SiteInstanceEditor.$editorIconType = "base-icon-gl";
